// Implements: F-IDN-014, F-UIX-009
/**
 * The enrolment QR, drawn by React from the `otpauth://` URI: `qrcode.create` answers the module
 * matrix and this component writes one `path` of 1x1 squares, so no markup string is ever injected
 * into the page. Medium error correction and a 4-module quiet zone, which every authenticator reads.
 *
 * Black on white in BOTH themes, on purpose: an inverted code (light on dark) is not read by every
 * authenticator, so these two are the one place the account pages do not use a theme alias. The
 * white quiet zone is the SVG's own rectangle, so the style sheet carries no colour.
 */

import { create } from "qrcode";
import { useMemo } from "react";
import type { ReactElement } from "react";

import styles from "./TotpQr.module.css";

const QUIET = 4;

/** The dark modules as one SVG path, offset by the quiet zone. */
export function qrPathOf(text: string): { readonly size: number; readonly path: string } {
  const { modules } = create(text, { errorCorrectionLevel: "M" });
  const parts: string[] = [];
  for (let row = 0; row < modules.size; row += 1) {
    for (let column = 0; column < modules.size; column += 1) {
      if (modules.get(row, column)) parts.push(`M${String(column + QUIET)} ${String(row + QUIET)}h1v1h-1z`);
    }
  }
  return { size: modules.size + 2 * QUIET, path: parts.join("") };
}

export interface TotpQrProps {
  readonly uri: string;
  readonly label: string;
}

export function TotpQr({ uri, label }: TotpQrProps): ReactElement {
  const { size, path } = useMemo(() => qrPathOf(uri), [uri]);
  return (
    <div className={styles.qr}>
      <svg viewBox={`0 0 ${String(size)} ${String(size)}`} shapeRendering="crispEdges" role="img" aria-label={label}>
        <rect width={size} height={size} fill="white" />
        <path d={path} fill="black" />
      </svg>
    </div>
  );
}
